import { AxiosInstance } from "axios"
import { Admin, CreateAdmin, CreateUser, UpdateAdmin, UpdateUser, User, UserSetting } from "./userTypes"
import { ServiceResponse } from "../../core/api/apiTypes"

export enum UserQuery {
    USER_DETAILS = "details-for-user",
    ADMIN_DETAILS = "details-for-admin",
}

// --- Create user ---
type CreateUserParams = Readonly<{
    user: CreateUser
}>

type UserResponse = Readonly<{
    user: User
}>

type CreateUserResponse = ServiceResponse<UserResponse>

export async function createUser(
    { user }: CreateUserParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateUserResponse> {
    const response = await axios.post(
        `/users`,
        {
            user,
        },
        {
            signal,
        },
    )

    return response.data
}

// --- Update user ---
type UpdateUserParams = Readonly<{
    userId: number
    user: UpdateUser
}>

type UpdateUserResponse = ServiceResponse<UserResponse>

export async function updateUser(
    { userId, user }: UpdateUserParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UpdateUserResponse> {
    const url = `/users/${userId}`

    const response = await axios.post(
        url,
        {
            user,
        },
        {
            signal,
        },
    )

    return response.data
}

// --- Delete user ---
type DeleteUserParams = Readonly<{
    userId: number
}>

type DeleteUserResponse = ServiceResponse<{}>

export async function deleteUser(
    { userId }: DeleteUserParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeleteUserResponse> {
    const url = `/users/${userId}`

    const response = await axios.delete(url, {
        signal,
    })

    return response.data
}

// --- Get user details
type GetUserDetailsParams = Readonly<{
    userId: number
}>

export type GetUserDetailsResponse = Readonly<{
    user: User
}>

export async function getUserDetails(
    { userId }: GetUserDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetUserDetailsResponse> {
    const url = `/users/${userId}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// --- Create admin ---
type CreateAdminParams = Readonly<{
    admin: CreateAdmin
}>

type AdminResponse = Readonly<{
    admin: Admin
}>

type CreateAdminResponse = ServiceResponse<AdminResponse>

export async function createAdmin(
    { admin }: CreateAdminParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateAdminResponse> {
    const response = await axios.post(
        `/administrators`,
        {
            admin,
        },
        {
            signal,
        },
    )

    return response.data
}

// --- Update admin ---
type UpdateAdminParams = Readonly<{
    userId: number
    admin: UpdateAdmin
}>

type UpdateAdminResponse = ServiceResponse<AdminResponse>

export async function updateAdmin(
    { userId, admin }: UpdateAdminParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<UpdateAdminResponse> {
    const url = `/administrators/${userId}`

    const response = await axios.post(
        url,
        {
            admin,
        },
        {
            signal,
        },
    )

    return response.data
}

// --- Delete admin ---
type DeleteAdminParams = Readonly<{
    userId: number
}>

type DeleteAdminResponse = ServiceResponse<{}>

export async function deleteAdmin(
    { userId }: DeleteAdminParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeleteAdminResponse> {
    const url = `/administrators/${userId}`

    const response = await axios.delete(url, {
        signal,
    })

    return response.data
}

// --- Get admin details
type GetAdminDetailsParams = Readonly<{
    userId: number
}>

export type GetAdminDetailsResponse = Readonly<AdminResponse>

export async function getAdminDetails(
    { userId }: GetAdminDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GetAdminDetailsResponse> {
    const url = `/administrators/${userId}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// --- Save admin user setting ---
type SaveAdminUserSettingsParams = Readonly<{
    userId: number
    userSettings: UserSetting
}>

type SaveAdminUserSettingsResponse = ServiceResponse<{}>

export async function saveAdminUserSettings(
    { userId, userSettings }: SaveAdminUserSettingsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SaveAdminUserSettingsResponse> {
    const url = `/administrators/${userId}/userSettings`

    const response = await axios.post(
        url,
        {
            ...userSettings,
        },
        {
            signal,
        },
    )

    return response.data
}
